import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { qk } from '../services/queryKeys';
import { fetchIdentifiers, upsertIdentifierApi } from '../services/api';
import { useAppStore } from '../store/useAppStore';
import Chip, { type ChipVariant } from './Chip';
import Button from './Button';
import Modal from './Modal';
import Empty from './Empty';

/**
 * Employee identifiers with expiry (SKPEOPLE-UICC-001 CH-U3).
 *
 * WHY THE WARN STATES MATTER: "an expired chauffeur permit must be visible
 * BEFORE dispatch refuses the driver, not after." A driver turned away at the
 * gate is a lost shift; an amber chip 60 days out is a renewal appointment.
 *
 * Renewals APPEND (CH-5.1), so superseded rows stay on screen struck through —
 * the same UX grammar as Billing's tax registrations.
 */

const WARN_DAYS = 60;

/** Days until expiry, or null when it never expires. */
function daysUntil(expiresOn: string | null): number | null {
  if (!expiresOn) return null;
  const ms = new Date(`${expiresOn}T00:00:00Z`).getTime() - Date.now();
  return Math.floor(ms / 86_400_000);
}

function expiryChip(expiresOn: string | null): { variant: ChipVariant; label: string } | null {
  const days = daysUntil(expiresOn);
  if (days === null) return null;
  if (days < 0) return { variant: 'danger', label: `expired ${expiresOn}` };
  if (days <= WARN_DAYS) return { variant: 'warn', label: `expires in ${days}d` };
  return { variant: 'neutral', label: `valid to ${expiresOn}` };
}

interface Props {
  employeeId: string;
  /** HR-only: only HR may record or renew an identifier. */
  canEdit: boolean;
}

export default function IdentifiersPanel({ employeeId, canEdit }: Props) {
  const { toast } = useAppStore();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ idType: '', value: '', issuingAuthority: '', expiresOn: '' });

  const { data: identifiers = [], isLoading, isError } = useQuery({
    queryKey: qk.identifiers(employeeId),
    queryFn: () => fetchIdentifiers(employeeId),
    enabled: !!employeeId,
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      upsertIdentifierApi(employeeId, {
        idType: form.idType.trim(),
        value: form.value.trim(),
        issuingAuthority: form.issuingAuthority.trim() || undefined,
        expiresOn: form.expiresOn || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.identifiers(employeeId) });
      queryClient.invalidateQueries({ queryKey: qk.complianceSnapshot(employeeId) });
      toast('Identifier recorded');
      setForm({ idType: '', value: '', issuingAuthority: '', expiresOn: '' });
      setOpen(false);
    },
    onError: (e: Error) => toast(e.message || 'Could not save identifier', 'danger'),
  });

  const current = identifiers.filter((i) => !i.supersededAt);
  const superseded = identifiers.filter((i) => i.supersededAt);

  return (
    <div className="t-card" style={{ marginBottom: 20 }}>
      <div className="card-body">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ fontSize: 'var(--fs-base)', fontWeight: 600 }}>Identifiers</h3>
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => setOpen(true)}>Record / renew</Button>
          )}
        </div>

        {isError ? (
          <p style={{ color: 'var(--danger, #dc2626)', fontSize: 'var(--fs-sm)' }}>
            Could not load identifiers.
          </p>
        ) : isLoading ? (
          <p style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>Loading…</p>
        ) : identifiers.length === 0 ? (
          <Empty title="No identifiers on record" description="Licences, permits and national IDs appear here." icon="doc" />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {current.map((i) => {
              const chip = expiryChip(i.expiresOn);
              return (
                <div key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', minWidth: 150 }}>
                    {i.idType.replace(/_/g, ' ').toLowerCase()}
                  </span>
                  {/* Plex Mono for identifiers, per the design system. */}
                  <span className="mono" style={{ fontWeight: 600 }}>{i.value}</span>
                  {chip && <Chip variant={chip.variant} dot>{chip.label}</Chip>}
                  {i.issuingAuthority && (
                    <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>{i.issuingAuthority}</span>
                  )}
                </div>
              );
            })}

            {superseded.length > 0 && (
              <>
                <div style={{ borderTop: '1px solid var(--border)', margin: '6px 0' }} />
                <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>Superseded</p>
                {superseded.map((i) => (
                  <div key={i.id} style={{ display: 'flex', alignItems: 'center', gap: 10, opacity: 0.6 }}>
                    <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)', minWidth: 150 }}>
                      {i.idType.replace(/_/g, ' ').toLowerCase()}
                    </span>
                    {/* Struck through: the history survives, it is just not current. */}
                    <span className="mono" style={{ textDecoration: 'line-through' }}>{i.value}</span>
                    {i.expiresOn && (
                      <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>to {i.expiresOn}</span>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {open && (
        <Modal
          title="Record or renew an identifier"
          onClose={() => setOpen(false)}
          footer={
            <>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button
                variant="accent"
                disabled={saveMutation.isPending}
                onClick={() => {
                  if (!form.idType.trim() || !form.value.trim()) {
                    toast('Type and value are required', 'warn'); return;
                  }
                  saveMutation.mutate();
                }}
              >
                Save
              </Button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
              Recording a type that already exists renews it: the current value is superseded and kept in history.
            </p>
            <label>
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>Type</span>
              <input
                className="t-input"
                placeholder="CHAUFFEUR_PERMIT, EMIRATES_ID, MOL_PERSON_ID…"
                value={form.idType}
                onChange={(e) => setForm({ ...form, idType: e.target.value })}
              />
            </label>
            <label>
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>Value</span>
              <input className="t-input mono" value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })} />
            </label>
            <label>
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>Issuing authority</span>
              <input className="t-input" value={form.issuingAuthority}
                onChange={(e) => setForm({ ...form, issuingAuthority: e.target.value })} />
            </label>
            <label>
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>Expires on</span>
              <input className="t-input" type="date" value={form.expiresOn}
                onChange={(e) => setForm({ ...form, expiresOn: e.target.value })} />
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}
